import { Plus, Trash2, FolderPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Combobox } from '@/components/ui/combobox'
import { MultiSelect } from '@/components/ui/multi-select'
import { cn } from '@/utils/cn'
import { isFilterGroup } from '@/types'
import type {
  FilterGroup,
  FilterCondition,
  FilterOperator,
  BooleanOp,
} from '@/types'
import {
  OPERATORS_BY_WIDGET,
  defaultOperatorFor,
  defaultValueFor,
  type FieldMeta,
} from './dagFilterUtils'

interface DagFilterExpressionBuilderProps {
  group: FilterGroup
  onChange: (group: FilterGroup) => void
  fieldMeta: Record<string, FieldMeta>
  fieldOrder: string[]
  depth?: number
  /** Present only for nested groups — removes this group from its parent. */
  onRemove?: () => void
}

interface ConditionValueInputProps {
  condition: FilterCondition
  meta: FieldMeta | undefined
  onChange: (value: string | string[]) => void
}

function ConditionValueInput({
  condition,
  meta,
  onChange,
}: ConditionValueInputProps) {
  const widget = meta?.widget ?? 'text'

  if (widget === 'dropdown') {
    const options = meta?.options ?? []
    if (condition.operator === 'in') {
      return (
        <MultiSelect
          className="w-full"
          options={options}
          value={Array.isArray(condition.value) ? condition.value : []}
          onValueChange={onChange}
          placeholder="Select values"
        />
      )
    }
    return (
      <Combobox
        className="w-full"
        options={options}
        value={
          typeof condition.value === 'string' && condition.value !== ''
            ? condition.value
            : null
        }
        onValueChange={v => onChange(v ?? '')}
        placeholder="Select value"
      />
    )
  }

  if (widget === 'boolean') {
    return (
      <Select
        value={typeof condition.value === 'string' ? condition.value : ''}
        onValueChange={onChange}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Value" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">Yes</SelectItem>
          <SelectItem value="false">No</SelectItem>
        </SelectContent>
      </Select>
    )
  }

  if (widget === 'number') {
    return (
      <Input
        type="number"
        className="w-full"
        placeholder="Value"
        value={
          typeof condition.value === 'string' ||
          typeof condition.value === 'number'
            ? String(condition.value)
            : ''
        }
        onChange={e => onChange(e.target.value)}
      />
    )
  }

  if (widget === 'date') {
    return (
      <Input
        type="date"
        className="w-full"
        value={typeof condition.value === 'string' ? condition.value : ''}
        onChange={e => onChange(e.target.value)}
      />
    )
  }

  return (
    <Input
      className="w-full"
      placeholder="Value"
      value={typeof condition.value === 'string' ? condition.value : ''}
      onChange={e => onChange(e.target.value)}
    />
  )
}

interface ConditionFieldSelectProps {
  value: string
  fieldOrder: string[]
  fieldMeta: Record<string, FieldMeta>
  onChange: (field: string) => void
}

function ConditionFieldSelect({
  value,
  fieldOrder,
  fieldMeta,
  onChange,
}: ConditionFieldSelectProps) {
  const options = fieldOrder.map(f => ({
    value: f,
    label: fieldMeta[f]?.label ?? f,
  }))
  return (
    <Combobox
      className="flex-1 min-w-[160px]"
      options={options}
      value={value || null}
      onValueChange={v => v && onChange(v)}
      placeholder="Select field"
    />
  )
}

/**
 * Recursive nested AND/OR filter-tree editor for line items, structurally
 * parallel to StoreModels' FilterExpressionBuilder (same proven interaction
 * model — AND/OR toggle, +Rule/+Group, nested indentation) but driven by
 * DagColumnMeta-derived field metadata instead of hierarchy/store-attribute
 * fields, with type-aware operator sets and value widgets per column.
 */
export function DagFilterExpressionBuilder({
  group,
  onChange,
  fieldMeta,
  fieldOrder,
  depth = 0,
  onRemove,
}: DagFilterExpressionBuilderProps) {
  const updateOp = (op: BooleanOp) => onChange({ ...group, op })

  const addCondition = () => {
    const field = fieldOrder[0]
    const widget = fieldMeta[field]?.widget ?? 'text'
    const operator = defaultOperatorFor(widget)
    const newCondition: FilterCondition = {
      field,
      operator,
      value: defaultValueFor(operator),
    }
    onChange({ ...group, conditions: [...group.conditions, newCondition] })
  }

  const addGroup = () => {
    const newGroup: FilterGroup = { op: 'AND', conditions: [] }
    onChange({ ...group, conditions: [...group.conditions, newGroup] })
  }

  const removeItem = (index: number) => {
    onChange({
      ...group,
      conditions: group.conditions.filter((_, i) => i !== index),
    })
  }

  const updateItem = (index: number, item: FilterCondition | FilterGroup) => {
    onChange({
      ...group,
      conditions: group.conditions.map((c, i) => (i === index ? item : c)),
    })
  }

  const updateConditionField = (index: number, field: string) => {
    const widget = fieldMeta[field]?.widget ?? 'text'
    const operator = defaultOperatorFor(widget)
    updateItem(index, { field, operator, value: defaultValueFor(operator) })
  }

  const updateConditionOperator = (
    index: number,
    condition: FilterCondition,
    operator: FilterOperator
  ) => {
    const wasMulti = condition.operator === 'in'
    const isMulti = operator === 'in'
    updateItem(index, {
      ...condition,
      operator,
      value: wasMulti !== isMulti ? defaultValueFor(operator) : condition.value,
    })
  }

  const updateConditionValue = (
    index: number,
    condition: FilterCondition,
    value: string | string[]
  ) => {
    updateItem(index, { ...condition, value })
  }

  return (
    <div
      className={cn(
        'rounded-md border border-border p-3',
        depth > 0 && 'ml-4 border-dashed',
        group.conditions.length === 0 && 'border-amber-400'
      )}
    >
      {/* Group header */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Select value={group.op} onValueChange={v => updateOp(v as BooleanOp)}>
          <SelectTrigger className="w-24">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="AND">AND</SelectItem>
            <SelectItem value="OR">OR</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">
          match {group.op === 'AND' ? 'all' : 'any'} of the following
        </span>

        <div className="ml-auto flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1 cursor-pointer"
            onClick={addCondition}
          >
            <Plus className="h-3 w-3" />
            Rule
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1 cursor-pointer"
            onClick={addGroup}
          >
            <FolderPlus className="h-3 w-3" />
            Group
          </Button>
          {onRemove && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 cursor-pointer text-muted-foreground hover:text-destructive"
              onClick={onRemove}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {depth > 0 && (
          <span className="text-xs text-muted-foreground w-full">
            Nested group
          </span>
        )}
      </div>

      {/* Conditions */}
      <div className="flex flex-col gap-2">
        {group.conditions.length === 0 && (
          <p className="text-xs py-2 text-amber-600">
            No rules yet. Add a rule or group.
          </p>
        )}

        {group.conditions.map((item, index) => {
          if (isFilterGroup(item)) {
            return (
              <DagFilterExpressionBuilder
                key={index}
                group={item}
                onChange={updated => updateItem(index, updated)}
                fieldMeta={fieldMeta}
                fieldOrder={fieldOrder}
                depth={depth + 1}
                onRemove={() => removeItem(index)}
              />
            )
          }

          const condition = item as FilterCondition
          const meta = fieldMeta[condition.field]
          const operators = OPERATORS_BY_WIDGET[meta?.widget ?? 'text']

          return (
            <div
              key={index}
              className="relative flex flex-col gap-2 rounded-md border border-border bg-muted/30 p-2.5 pr-10"
            >
              <div className="flex items-center gap-2">
                <ConditionFieldSelect
                  value={condition.field}
                  fieldOrder={fieldOrder}
                  fieldMeta={fieldMeta}
                  onChange={v => updateConditionField(index, v)}
                />

                <Select
                  value={condition.operator}
                  onValueChange={v =>
                    updateConditionOperator(
                      index,
                      condition,
                      v as FilterOperator
                    )
                  }
                >
                  <SelectTrigger className="w-32 shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {operators.map(op => (
                      <SelectItem key={op.value} value={op.value}>
                        {op.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <ConditionValueInput
                condition={condition}
                meta={meta}
                onChange={v => updateConditionValue(index, condition, v)}
              />

              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 h-7 w-7 p-0 cursor-pointer text-muted-foreground hover:text-destructive"
                onClick={() => removeItem(index)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
